// Pure helpers for the CRM's "Set up" on a paid hosting or email purchase the
// customer never finished (components/PendingSetupForm.jsx).
//
// The rules mirror squish-backend's routes/hosting-setup.js and
// routes/email-setup.js submit validation, so the common typo gets a field
// message here instead of a round trip. The backend still checks everything.

import { normaliseGoLiveDomain } from './hostingGoLive.js';

// Managed WordPress and AI quick-start install into a real domain; general
// hosting may start on a temporary address and go live later.
export function hostingNeedsDomain(setupType) {
  return setupType === 'wordpress' || setupType === 'ai-quick';
}

export function validateHostingSetup({ domain, setupType }) {
  const raw = String(domain || '').trim();
  if (!raw) {
    if (hostingNeedsDomain(setupType)) return { fields: null, error: 'This plan needs the customer\'s domain.' };
    return { fields: { domain: '' }, error: '' };
  }
  const { domain: d, error } = normaliseGoLiveDomain(raw);
  if (error) return { fields: null, error };
  return { fields: { domain: d }, error: '' };
}

const PREFIX_RE = /^[a-z0-9]([a-z0-9-]{0,18}[a-z0-9])?$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateEmailSetup(form) {
  const f = Object.fromEntries(Object.entries(form || {}).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]));
  if (f.branch !== 'new-tenant' && f.branch !== 'existing-tenant') {
    return { fields: null, error: 'Choose a new or an existing Microsoft 365 tenant.' };
  }
  const { domain, error: domainError } = normaliseGoLiveDomain(f.domain);
  if (!f.domain) return { fields: null, error: 'The customer\'s mail domain is required.' };
  if (domainError) return { fields: null, error: domainError };

  if (f.branch === 'new-tenant') {
    const missing = [['onmicrosoftPrefix', 'onmicrosoft name'], ['adminUpn', 'admin username'], ['adminFirstName', 'admin first name'], ['adminLastName', 'admin last name']]
      .filter(([k]) => !f[k]).map(([, label]) => label);
    if (missing.length) return { fields: null, error: `Missing: ${missing.join(', ')}.` };
    if (!PREFIX_RE.test(f.onmicrosoftPrefix)) {
      return { fields: null, error: 'The onmicrosoft name is 1-20 letters, digits or hyphens, not starting or ending with a hyphen.' };
    }
    if (f.adminUpn.includes('@')) return { fields: null, error: 'The admin username is the part before the @.' };
    return {
      fields: {
        branch: 'new-tenant', domain,
        onmicrosoftPrefix: f.onmicrosoftPrefix.toLowerCase(), adminUpn: f.adminUpn,
        adminFirstName: f.adminFirstName, adminLastName: f.adminLastName,
      },
      error: '',
    };
  }

  if (!UUID_RE.test(f.customerTenantId || '')) return { fields: null, error: 'The tenant id is a GUID, like 1b2c3d4e-....' };
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.customerAdminEmail || '')) return { fields: null, error: 'Enter the tenant admin\'s email address.' };
  return {
    fields: { branch: 'existing-tenant', domain, customerTenantId: f.customerTenantId.toLowerCase(), customerAdminEmail: f.customerAdminEmail },
    error: '',
  };
}
